import { usePurchaserId } from '@openeducation/hr-rn-cognito-providers';
import {
  NUSchoolsOverview,
  NUSchoolDetail,
  HRMonthlyAchievements,
  HRHeading,
  NUActivitiesOverview,
  HRMostActiveStudents,
  HRLeastActiveStudents,
  useHRPortalTranslation,
  NUSchoolsSelect,
  useNUMonthlyAchievements,
  useSchool,
  useSchoolsSelect,
  useLeastActiveStudents,
  useLastSyncText,
  useNUMostActiveStudents,
  usePeriodReport,
  useReport,
  SectionPlaceholder,
  flex,
  mb,
  position,
  zIndex,
} from '@openeducation/hr-rn-components';
import {
  hrManagerAtom,
  useNextuActivityIntervals,
  useNextuActivitiesOverview,
  useNextuSchoolsOverview,
  useNextuSchoolDetail,
  useNextuLeastActiveStudents,
  nextuActivityIntervalDatesAtom,
} from '@openeducation/hr-rn-graphql-providers';
import {
  nextUSimplifiedAtom,
  schoolAtom,
} from '@openeducation/hr-rn-jotai-atoms';
import {
  NextuActivityType,
  ReportType,
} from '@openeducation/hr-rn-shared-types';
import { styled, css } from '@openeducation/hr-rn-themes';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useState } from 'react';
import { View, LayoutChangeEvent } from 'react-native';

import StudentsHorizontalBarChart from '../../components/charts/students-horizontal-bar-chart.web';
import NUStatisticsOfActivities from '../../components/statistics-of-activities/nu-statistics-of-activities';
import { saveFile } from '../../helpers/save-file';
import { useScrollPosition } from '../../hooks/use-scroll-position.web';
import { useStudentsListProps } from '../../hooks/use-students-list-props';
import { NUPath } from '../../router';

const NUHomeScreen = () => {
  const isNextUSimplified = useAtomValue(nextUSimplifiedAtom);
  const [schoolsSelectHeight, setSchoolsSelectHeight] = useState<number>(0);
  const [inactiveDaysThreshold, setInactiveDaysThreshold] =
    useState<number>(30);

  const hrManager = useAtomValue(hrManagerAtom);
  const { t } = useHRPortalTranslation();
  const purchaserId = usePurchaserId();
  const { school, schoolName, schoolId } = useSchool();
  const { lastSyncText } = useLastSyncText();
  const {
    loading: activitiesOverviewLoading,
    data: activitiesOverviewData,
    isError: activitiesOverviewError,
  } = useNextuActivitiesOverview({
    purchaserId,
    schoolId,
  });
  const setSchool = useSetAtom(schoolAtom);
  const schoolsSelectProps = useSchoolsSelect({
    setSchool,
    school,
  });
  const { data: schoolsOverviewData, loading: schoolsOverviewLoading } =
    useNextuSchoolsOverview({
      purchaserId,
    });
  const { data: schoolDetailData, loading: schoolDetailLoading } =
    useNextuSchoolDetail({
      purchaserId,
      schoolId,
    });

  const { mostActiveStudentsChartData, ...mostActiveStudentsProps } =
    useNUMostActiveStudents();
  useNextuActivityIntervals({ purchaserId, schoolId });
  const { scrollPosition } = useScrollPosition();
  const isSchoolSelectFixed = scrollPosition > 350;
  const nextuActivityIntervalDates = useAtomValue(
    nextuActivityIntervalDatesAtom
  );

  const monthlyAchievementsProps = useNUMonthlyAchievements();

  const leastActiveStudentsData15 = useNextuLeastActiveStudents({
    purchaserId,
    schoolId,
    inactiveDaysThreshold: 15,
  });
  const leastActiveStudentsData30 = useNextuLeastActiveStudents({
    purchaserId,
    schoolId,
    inactiveDaysThreshold: 30,
  });
  const leastActiveStudentsData =
    inactiveDaysThreshold === 15
      ? leastActiveStudentsData15
      : leastActiveStudentsData30;

  const leastActiveStudentsProps = useLeastActiveStudents(
    leastActiveStudentsData
  );
  const hasLeastActiveStudents =
    (leastActiveStudentsData30?.data || []).length > 0 ||
    (leastActiveStudentsData15?.data || []).length > 0;

  const studentsListProps = useStudentsListProps(NUPath.Student);
  const historicalReport = useReport(saveFile, ReportType.HISTORICAL);
  const monthlyReport = usePeriodReport(
    saveFile,
    ReportType.MONTHLY,
    nextuActivityIntervalDates
  );
  const liveClassesReport = usePeriodReport(
    saveFile,
    ReportType.MONTHLY_LIVE_CLASSES,
    nextuActivityIntervalDates
  );

  const onSchoolsSelectLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const height = event?.nativeEvent?.layout?.height;
      if (height && schoolsSelectHeight === 0) {
        setSchoolsSelectHeight(height);
      }
    },
    [schoolsSelectHeight]
  );

  const renderSchoolsOverview = () => {
    if (
      !schoolsOverviewLoading &&
      !schoolDetailLoading &&
      (schoolsOverviewData?.schoolStatistics || []).length === 0
    ) {
      return null;
    }

    return schoolId ? (
      <NUSchoolDetail
        data={schoolsOverviewData}
        courseData={schoolDetailData}
        schoolId={schoolId}
        loading={schoolsOverviewLoading || schoolDetailLoading}
      />
    ) : (
      <NUSchoolsOverview
        data={schoolsOverviewData}
        loading={schoolsOverviewLoading}
      />
    );
  };
  const activitiesOverviewTitle = (): string => {
    if (isNextUSimplified) {
      return 'home.activitiesOverview.titleSimplified';
    }
    if (schoolName) {
      return 'home.activitiesOverview.titleSchool';
    }
    return 'home.activitiesOverview.title';
  };

  const activitiesOverviewSubtitle = (): string => {
    if (isNextUSimplified) {
      return 'home.activitiesOverview.subtitleSimplified';
    }
    if (schoolName) {
      return 'home.activitiesOverview.subtitleSchool';
    }
    return 'home.activitiesOverview.subtitle';
  };
  return (
    <>
      <HRHeading
        title={t('home.heading.title', { managerName: hrManager?.name })}
        description={`${t('home.heading.description')} ${lastSyncText}`}
        studentsListProps={studentsListProps}
        historicalReport={historicalReport}
        monthlyReport={monthlyReport}
        liveClassesReport={liveClassesReport}
      />
      {!isNextUSimplified && (
        <>
          {isSchoolSelectFixed && (
            <SchoolsSelectSpacer height={schoolsSelectHeight} />
          )}
          <View style={[position('relative'), zIndex(1)]}>
            <NUSchoolsSelect
              {...schoolsSelectProps}
              onLayout={onSchoolsSelectLayout}
              fixed={isSchoolSelectFixed}
            />
          </View>
        </>
      )}

      {!activitiesOverviewError && (
        <NUActivitiesOverview
          isLoading={activitiesOverviewLoading}
          stats={activitiesOverviewData}
          title={t(activitiesOverviewTitle())}
          subtitle={t(activitiesOverviewSubtitle())}
        />
      )}
      {!isNextUSimplified && renderSchoolsOverview()}
      <NUStatisticsOfActivities />
      <NUMonthlyAchievements<NextuActivityType> {...monthlyAchievementsProps} />
      <NUMostActiveStudents {...mostActiveStudentsProps}>
        <View style={[flex('flex-1'), mb(15)]}>
          {mostActiveStudentsChartData.values.length === 0 ? (
            <SectionPlaceholder text={t('common.chart.noData')} />
          ) : (
            <StudentsHorizontalBarChart {...mostActiveStudentsChartData} />
          )}
        </View>
      </NUMostActiveStudents>
      {hasLeastActiveStudents && (
        <NULeastActiveStudents
          {...leastActiveStudentsProps}
          onInactiveDaysChanges={setInactiveDaysThreshold}
          title={
            schoolName
              ? t('leastActiveStudents.title.school', {
                  schoolName,
                  count: inactiveDaysThreshold,
                })
              : t('leastActiveStudents.title.all', {
                  count: inactiveDaysThreshold,
                })
          }
        />
      )}
    </>
  );
};

const borderStyle = css`
  border-width: 1px;
  border-color: ${({ theme }) => theme.color.border};
`;

const NUMonthlyAchievements = styled(HRMonthlyAchievements)`
  ${borderStyle}
` as typeof HRMonthlyAchievements;

const NUMostActiveStudents = styled(HRMostActiveStudents)`
  ${borderStyle}
`;

const NULeastActiveStudents = styled(HRLeastActiveStudents)`
  ${borderStyle}
`;

const SchoolsSelectSpacer = styled.View<{ height: number }>`
  height: ${({ height }) => height}px;
`;

export default NUHomeScreen;
